<template>
  <div class="container mx-auto px-4 py-6">
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8">
      <!-- Header with gradient background -->
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3">
        <div class="flex items-center justify-between">
          <h1 class="font-semibold text-white text-lg">
            {{ $t('Mobile.MobileDiaryEntry.title') }}
          </h1>

          <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-white">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
          </div>
        </div>
      </div>

      <div class="p-4 space-y-4">
        <!-- Date Display -->
        <div class="bg-gray-50 rounded-lg p-3">
          <div class="flex items-center space-x-2">
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-500">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

            <span class="text-sm font-medium text-gray-600">{{ $t('Mobile.MobileDiaryEntry.date') }}</span>

            <span class="text-sm font-semibold text-gray-900">
              {{ diaryEntry ? diaryEntry.createdAt.toLocaleString() : (new Date()).toLocaleDateString() }}
            </span>
          </div>
        </div>

        <!-- Form for creating/editing -->
        <form
          v-if="!diaryEntry || (recordIsToday && diaryEntry.user_id === userStore.user.id)"
          class="space-y-4"
          @submit.prevent="saveEntry"
        >
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileDiaryEntry.entryContent') }}
            </label>

            <textarea
              v-model="entryForm.form.body"
              rows="12"
              name="body"
              :placeholder="$t('Mobile.MobileDiaryEntry.placeholder')"
              class="form-input resize-none"
            />

            <span
              v-if="entryForm.hasErrors('body')"
              class="text-red-600 text-sm flex items-center space-x-1"
            >
              <span>{{ entryForm.getErrors('body') }}</span>
            </span>
          </div>

          <!-- Action Buttons -->
          <div class="flex space-x-3 pt-2">
            <button
              type="button"
              class="btn btn--secondary-blue flex-1 flex items-center justify-center space-x-2"
              @click="router.push({ name: 'MobileDiaryIndex' })"
            >
              <span>{{ $t('Mobile.MobileDiaryEntry.cancel') }}</span>
            </button>

            <button
              type="submit"
              class="btn btn--primary-blue flex-1 flex items-center justify-center space-x-2"
            >
              <span>{{ diaryEntry ? $t('Mobile.MobileDiaryEntry.update') : $t('Mobile.MobileDiaryEntry.create') }}</span>
            </button>
          </div>
        </form>

        <!-- Read-only view for existing entries -->
        <template v-else-if="diaryEntry">
          <div class="space-y-4">
            <div class="bg-blue-50 rounded-lg p-4">
              <div class="flex items-start space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                <div class="flex-1">
                  <h3 class="text-sm font-medium text-blue-800 mb-2">
                    {{ $t('Mobile.MobileDiaryEntry.entryContent') }}
                  </h3>

                  <div class="text-sm text-blue-900 whitespace-pre-wrap break-words">
                    {{ diaryEntry.body }}
                  </div>
                </div>
              </div>
            </div>

            <div class="flex justify-center">
              <button
                type="button"
                class="btn btn--secondary-blue flex items-center space-x-2"
                @click="router.back()"
              >
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
</svg>

                <span>{{ $t('Mobile.MobileDiaryEntry.back') }}</span>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import { useDiaryEntriesStore } from '@/store/diaryEntries';
import { useNavStore } from '@/store/nav';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  diaryId?: string;
}>();

const diaryEntriesStore = useDiaryEntriesStore();
const projectsStore = useProjectsStore();
const router = useRouter();

const project = computed(() => {
  return projectsStore.project;
});

const diaryEntry = computed(() => {
  return diaryEntriesStore.models.where('id', parseInt(props.diaryId)).first();
});

const entryForm = useFormData({
  body: '',
  raise_level: false,
});

if(props.diaryId) {
  diaryEntriesStore.fetchDiaryEntry(props.diaryId);
}

watch(diaryEntry, (newValue) => {
  if(newValue) {
    entryForm.setData(diaryEntry.value);
  }
}, { immediate: true });

const createDiaryEntryAction = useStoreApiAction(diaryEntriesStore.createDiaryEntry);
const updateDiaryEntryAction = useStoreApiAction(diaryEntriesStore.updateDiaryEntry);

const saveEntry = () => {
  if(diaryEntry.value) {
    updateDiaryEntryAction.request(diaryEntry.value.id, entryForm.form).then(() => {
      Swal.fire({
        icon: 'success',
        title: t('Mobile.MobileDiaryEntry.entryUpdated'),
      }).then(() => {
        router.push({ name: 'MobileDiaryIndex' });
      });
    }).catch((error) => {
      entryForm.setErrors(error.data);
    });
  } else {
    Swal.fire({
      icon: 'question',
      title: t('Mobile.MobileDiaryEntry.sendTicketQuestion'),
      showDenyButton: true,
      showConfirmButton: true,
      confirmButtonColor: 'green',
      confirmButtonText: t('Mobile.MobileDiaryEntry.yes'),
    }).then((result) => {
      if(result.isConfirmed) {
        entryForm.form.raise_level = true;
      }

      createDiaryEntryAction.request(project.value.id, entryForm.form).then(() => {
        entryForm.resetData();

        Swal.fire({
          icon: 'success',
          title: t('Mobile.MobileDiaryEntry.entryCreated'),
        }).then(() => {
          router.push({ name: 'MobileDiaryIndex' });
        });
      }).catch((error) => {
        entryForm.setErrors(error.data);
      });
    });
  }
};

const recordIsToday = computed(() => {
  return diaryEntry.value.createdAt.hasSame(DateTime.local(), 'day');
});

const userStore = useUserStore();
const navStore = useNavStore();

navStore.backRoute = { name: 'MobileDiaryIndex' };
</script>

<style scoped></style>
